import React, { useCallback, useState } from "react";
import { Button, Flex, TableCell, TableRow, Text, Textarea } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors, Sizes } from "@jsq/ds/styles";
import { checkBadge, humanizeLabel, type CheckBadge } from "#utils/checksStatus";
import { CheckIssuesTable } from "../CheckIssuesTable";
import { ClearFeedbackModal } from "../ClearFeedbackModal";
import { FootingCaption, FootingDetailsTable } from "../FootingDetailsTable";
import * as styles from "./styles_vanilla.css";
import type { FeedbackRating } from "#graphql/supergraphTypes";
import type { CheckItem, ChecksTriageAction } from "#viewModelTypes";

export type CheckRowProps = {
  check: CheckItem;
  /** True while a triage write for THIS check is in flight — disables its controls. */
  isTriaging: boolean;
  onTriage: (findingId: string, action: ChecksTriageAction) => void;
  /** True while a feedback write for THIS check is in flight — disables the
   *  Submit button. */
  isSubmittingFeedback: boolean;
  onSubmitFeedback: (findingId: string, rating: FeedbackRating, note?: string | null) => void;
};

/** Tighter than the smallest spacing token (4px) on purpose: a label and the
 *  value beneath it read as one block, not two stacked rows. */
const LABEL_VALUE_GAP = 2;

/** Stacks the blocks inside the expanded detail. Between `Sizes.SM` (8) and
 *  `Sizes.MD` (16): 8 crowds the nested tables against the message, 16 opens a
 *  gap wide enough that the detail stops reading as one unit. */
const DETAIL_BLOCK_GAP = 12;

const TONE_CLASS: Record<CheckBadge["tone"], string> = {
  success: styles.badgeSuccess,
  warning: styles.badgeWarning,
  danger: styles.badgeDanger,
  neutral: styles.badgeNeutral,
};

const TONE_COLOR: Record<CheckBadge["tone"], Colors> = {
  success: Colors.Success500,
  warning: Colors.Warning600,
  danger: Colors.Danger500,
  neutral: Colors.Neutral500,
};

/** Result badge: icon + toned label. */
const ResultBadge: React.FC<{ check: CheckItem }> = ({ check }) => {
  const badge = checkBadge(check);
  return (
    <span className={`${styles.badge} ${TONE_CLASS[badge.tone]}`}>
      <Icon type={badge.icon} color={TONE_COLOR[badge.tone]} size="small" />
      {badge.text}
    </span>
  );
};

/** A single labeled value (Expected / Found). */
const LabeledValue: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Flex flexDirection="column" gap={LABEL_VALUE_GAP}>
    <Text variant="secondary-sm" asSpan>
      {label}
    </Text>
    <Text variant="regular-md" asSpan>
      {value}
    </Text>
  </Flex>
);

/**
 * One check as a row in the section's Check / Result table: name + a status
 * badge, expanding to the message, expected/found values, triage controls
 * (Ignore / Mark as issue), and per-check feedback capture. Provenance is implied
 * by the enclosing document → section, so no per-value source links are shown.
 */
export const CheckRow: React.FC<CheckRowProps> = ({
  check,
  isTriaging,
  onTriage,
  isSubmittingFeedback,
  onSubmitFeedback,
}) => {
  const { findingId } = check;
  const hasValues = check.expectedValue != null || check.actualValue != null;
  const hasFootingDetails =
    check.kind === "FOOTING" &&
    check.componentValues != null &&
    check.componentValues.length > 0;
  // Fallback for historical findings enumerated before `componentValues` shipped:
  // degrade to the row-label / delta caption alongside message + expected/actual.
  const hasFootingCaption =
    check.kind === "FOOTING" &&
    !hasFootingDetails &&
    (check.rowLabel != null || check.delta != null);
  const hasIssues = check.kind === "VISUAL" && check.issues.length > 0;
  const showValues = hasValues && !hasFootingDetails && !hasIssues;

  // Per-check feedback capture. `isFlagged` is server truth — read directly from
  // `check.feedback`, the same shape triage reads `check.triageState` from the
  // query result, so a previously-flagged check renders flagged on mount instead
  // of resetting to unflagged. `showForm` and `note` are local UI state seeded
  // once from that same server truth: `note` is a controlled textarea the
  // reviewer edits before an explicit Submit, so it intentionally doesn't
  // re-sync on a later poll (that would clobber an in-progress edit).
  const isFlagged = check.feedback !== null;
  const [showFeedbackForm, setShowFeedbackForm] = useState(isFlagged);
  const [note, setNote] = useState(check.feedback?.note ?? "");
  // A flagged check starts expanded so the reviewer's existing note is visible
  // without an extra click to open the row.
  const [expanded, setExpanded] = useState(isFlagged);
  // Clearing feedback is guarded by a confirm dialog so a reviewer can't
  // discard their thumbs-down (and any note) with a single accidental click.
  const [showClearModal, setShowClearModal] = useState(false);
  // When the server truth flips this row from flagged to unflagged — a clear
  // that actually landed (cache patch) or a refetch — collapse the note draft
  // back to the idle state. A failed clear leaves the row flagged, so the form
  // and the reviewer's note stay put and remain visible. Adjusting state during
  // render on a changed value is React's recommended alternative to a syncing
  // effect.
  const [prevIsFlagged, setPrevIsFlagged] = useState(isFlagged);
  if (prevIsFlagged !== isFlagged) {
    setPrevIsFlagged(isFlagged);
    if (!isFlagged) {
      setShowFeedbackForm(false);
      setNote("");
    }
  }

  const handleThumbsDownClick = useCallback(
    (e: React.MouseEvent) => {
      // The row is clickable to toggle expansion; the button must not also
      // trigger that.
      e.stopPropagation();
      if (findingId == null) {
        return;
      }
      if (isFlagged) {
        // Clearing is a deliberate action — confirm before submitting the
        // clearing rating: NONE (see clearFeedback).
        setShowClearModal(true);
        return;
      }
      if (showFeedbackForm) {
        // Unflagged with the draft note form already open (the red-outline
        // state): a second click cancels the draft, resetting the button to
        // grey and discarding the unsaved note. This is the only way an
        // in-progress draft returns to grey — a submitted draft turns solid red
        // via isFlagged instead.
        setShowFeedbackForm(false);
        setNote("");
        return;
      }
      setShowFeedbackForm(true);
      setExpanded(true);
    },
    [findingId, isFlagged, showFeedbackForm],
  );

  const clearFeedback = useCallback(() => {
    if (findingId == null) {
      return;
    }
    // Submit the clear, but don't optimistically collapse the note form here: if
    // the write fails the row stays flagged (server truth) and the reviewer's
    // note must remain visible. The form/note reset is driven off the server
    // truth flipping to unflagged (see prevIsFlagged above), so it only happens
    // on a clear that actually landed.
    onSubmitFeedback(findingId, "NONE");
  }, [findingId, onSubmitFeedback]);

  const handleSubmitFeedback = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (findingId == null) {
        return;
      }
      onSubmitFeedback(findingId, "DOWN", note);
    },
    [findingId, note, onSubmitFeedback],
  );

  // Triage controls by state: a confirmed check shows a "Marked as issue" pill
  // + Undo; an ignored check shows nothing; an untriaged failing check offers
  // Ignore / Mark as issue. Passing/skipped untriaged checks offer nothing.
  const triage: JSX.Element | null = ((): JSX.Element | null => {
    if (findingId == null) {
      return null;
    }
    if (check.triageState === "CONFIRMED_AS_ISSUE") {
      return (
        <div className={styles.triageActions}>
          <span className={styles.markedPill}>
            <Icon type="exclamation-triangle" color={Colors.Danger600} size="small" />
            Marked as issue
          </span>
          <Button
            displayAsLink
            isInline
            isLoading={isTriaging}
            onClick={() => onTriage(findingId, "UNTRIAGE")}
          >
            Undo
          </Button>
        </div>
      );
    }
    // Ignored is terminal (no controls); passing/skipped untriaged checks have
    // nothing to triage — only an untriaged failure offers Ignore / Mark.
    if (check.triageState === "IGNORED" || check.outcome !== "FAIL") {
      return null;
    }
    return (
      <div className={styles.triageActions}>
        <Button
          variant="neutral"
          icon="ban"
          isLoading={isTriaging}
          onClick={() => onTriage(findingId, "IGNORE")}
        >
          Ignore this run
        </Button>
        <Button
          variant="danger"
          icon="exclamation-triangle"
          isLoading={isTriaging}
          onClick={() => onTriage(findingId, "CONFIRM_AS_ISSUE")}
        >
          Mark as issue
        </Button>
      </div>
    );
  })();

  const hasDetails =
    Boolean(check.message) ||
    hasValues ||
    hasFootingDetails ||
    hasFootingCaption ||
    hasIssues ||
    triage !== null ||
    showFeedbackForm;

  const toggle = (): void => {
    if (hasDetails) {
      setExpanded((v) => !v);
    }
  };

  // Thumbs-down has three visual states:
  //   • grey outline   — no feedback and no open draft (idle).
  //   • red outline    — a draft is in progress: the note form is open for an
  //                      unflagged check (isDraft), i.e. the reviewer is
  //                      entering a note but hasn't submitted. Resets to grey by
  //                      cancelling the draft (second thumbs-down click); turns
  //                      solid red once the submit succeeds and isFlagged flips.
  //   • solid red      — feedback submitted (server truth: check.feedback !== null).
  const isDraft = showFeedbackForm && !isFlagged;
  const showFeedbackRed = isFlagged || isDraft;

  const thumbsDownLabel = ((): string => {
    if (isFlagged) {
      return "Clear feedback";
    }
    if (isDraft) {
      return "Cancel feedback";
    }
    return "Rate this check as unhelpful";
  })();

  return (
    <>
      {/* Clicking anywhere on the row toggles it, but that stays a mouse
          affordance: the row keeps its native `row`/`cell` semantics rather than
          taking `role="button"`, whose presentational children would flatten the
          cells and hide the nested feedback control from assistive tech. The
          chevron below is the real, focusable disclosure. */}
      <TableRow
        onClick={hasDetails ? toggle : undefined}
        className={hasDetails ? styles.clickableRow : undefined}
      >
        <TableCell className={styles.checkColumn}>
          <Flex alignItems="center" gap={Sizes.SM}>
            {hasDetails ? (
              <button
                type="button"
                aria-expanded={expanded}
                // Deliberately stable: `aria-expanded` already carries the open
                // state, so folding it into the name too would announce it twice.
                aria-label={`Toggle details for ${humanizeLabel(check.checkName)}`}
                // The row toggles on click too, so let this one win rather than
                // both firing and cancelling each other out.
                onClick={(e) => {
                  e.stopPropagation();
                  toggle();
                }}
                className={styles.disclosureButton}
              >
                <Icon
                  type={expanded ? "chevron-down" : "chevron-right"}
                  size="small"
                  color={Colors.Neutral500}
                />
              </button>
            ) : (
              <span className={styles.chevronSpacer} />
            )}
            <Text asSpan variant="regular-sm">
              {humanizeLabel(check.checkName)}
            </Text>
          </Flex>
        </TableCell>
        <TableCell className={styles.resultColumn}>
          <ResultBadge check={check} />
        </TableCell>
        <TableCell className={styles.feedbackColumn}>
          {findingId != null && (
            <button
              type="button"
              title={thumbsDownLabel}
              aria-label={thumbsDownLabel}
              aria-pressed={isFlagged}
              // Disabled while this row's feedback write is in flight so a
              // second click can't reopen the confirm modal or cancel a draft
              // whose write is still landing (matches the triage/Submit gating).
              disabled={isSubmittingFeedback}
              onClick={handleThumbsDownClick}
              className={
                showFeedbackRed
                  ? `${styles.feedbackIconButton} ${styles.feedbackIconButtonActive}`
                  : styles.feedbackIconButton
              }
            >
              <Icon
                type="thumbs-down"
                size="small"
                variant={isFlagged ? "solid" : "outline"}
                color={showFeedbackRed ? Colors.Danger500 : Colors.Neutral500}
              />
            </button>
          )}
        </TableCell>
      </TableRow>

      {hasDetails && expanded && (
        <TableRow>
          <TableCell colSpan={3}>
            <Flex flexDirection="column" gap={DETAIL_BLOCK_GAP} className={styles.detail}>
              {check.message && (
                <Text variant="secondary-md" asSpan>
                  {check.message}
                </Text>
              )}

              {hasFootingDetails && check.componentValues != null && (
                <FootingDetailsTable
                  componentValues={check.componentValues}
                  expectedValue={check.expectedValue}
                  actualValue={check.actualValue}
                  outcome={check.outcome}
                  checkName={check.checkName}
                  rowLabel={check.rowLabel}
                  delta={check.delta}
                />
              )}

              {hasFootingCaption && (
                <FootingCaption
                  rowLabel={check.rowLabel}
                  delta={check.delta}
                  isPercentage={check.checkName.includes("%")}
                />
              )}

              {hasIssues && <CheckIssuesTable issues={check.issues} />}

              {showValues && (
                <Flex gap={Sizes.LG}>
                  {check.expectedValue != null && (
                    <LabeledValue label="Expected" value={check.expectedValue} />
                  )}
                  {check.actualValue != null && (
                    <LabeledValue label="Found" value={check.actualValue} />
                  )}
                </Flex>
              )}

              {triage}

              {findingId != null && showFeedbackForm && (
                <div className={styles.feedbackArea}>
                  <Textarea
                    name={`feedback-${check.checkId}`}
                    placeholder="Add feedback note (optional)"
                    isFullWidth
                    value={note}
                    onSetValue={setNote}
                    data-testid={`feedback-note-${check.checkId}`}
                  />
                  <div className={styles.feedbackSubmitRow}>
                    <Button
                      variant="primary"
                      isLoading={isSubmittingFeedback}
                      onClick={handleSubmitFeedback}
                    >
                      Submit feedback
                    </Button>
                  </div>
                </div>
              )}
            </Flex>
          </TableCell>
        </TableRow>
      )}

      <ClearFeedbackModal
        isOpen={showClearModal}
        checkName={humanizeLabel(check.checkName)}
        onCancel={() => setShowClearModal(false)}
        onConfirm={clearFeedback}
      />
    </>
  );
};
